import { useEffect, useState } from 'react';
import {
  addComment,
  deleteComment,
  listComments,
  updateComment,
  type TaskComment,
} from '../../lib/tasks';
import { useAuth } from '../../lib/auth';
import { memberName } from '../../lib/users';
import { Alert, Button, SkeletonRows, Textarea } from '../ui';
import { Avatar } from './TaskBadges';

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export function TaskComments({
  taskId,
  onCountChange,
}: {
  taskId: string;
  onCountChange: (count: number) => void;
}) {
  const { user } = useAuth();
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    listComments(taskId, controller.signal)
      .then((data) => {
        setComments(data.comments);
        setError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setError(requestError instanceof Error ? requestError.message : 'Could not load comments.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [taskId]);

  function apply(next: TaskComment[]) {
    setComments(next);
    onCountChange(next.length);
  }

  async function post() {
    const body = draft.trim();
    if (!body || saving) return;

    setSaving(true);
    setError(null);
    try {
      const { comment } = await addComment(taskId, body);
      setDraft('');
      apply([...comments, comment]);
    } catch (postError: unknown) {
      setError(postError instanceof Error ? postError.message : 'Could not post that comment.');
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit(commentId: string) {
    const body = editDraft.trim();
    if (!body) return;

    try {
      const { comment } = await updateComment(taskId, commentId, body);
      setEditingId(null);
      apply(comments.map((entry) => (entry.id === commentId ? comment : entry)));
    } catch (editError: unknown) {
      setError(editError instanceof Error ? editError.message : 'Could not save that edit.');
    }
  }

  async function remove(commentId: string) {
    try {
      await deleteComment(taskId, commentId);
      apply(comments.filter((entry) => entry.id !== commentId));
    } catch (deleteError: unknown) {
      setError(deleteError instanceof Error ? deleteError.message : 'Could not delete that comment.');
    }
  }

  return (
    <section>
      <h3 className="text-dense font-semibold tracking-tight text-stone-700">
        Comments {comments.length > 0 ? <span className="text-stone-500">{comments.length}</span> : null}
      </h3>

      {error ? (
        <div className="mt-2">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {loading ? (
        <SkeletonRows count={2} rowClassName="h-8" />
      ) : (
        <ul className="mt-2 space-y-2.5">
          {comments.map((comment) => (
            <li key={comment.id} className="flex gap-2">
              <Avatar
                label={memberName(comment.author)}
                pictureUrl={comment.author?.pictureUrl}
                className="h-5 w-5"
              />
              <div className="min-w-0 flex-1">
                <p className="text-meta text-stone-500">
                  <span className="font-medium text-stone-700">
                    {comment.author?.name ?? comment.author?.email ?? 'Unknown'}
                  </span>{' '}
                  {formatWhen(comment.createdAt)}
                  {comment.editedAt ? <span className="text-stone-500"> · edited</span> : null}
                </p>

                {editingId === comment.id ? (
                  <div className="mt-1 space-y-1.5">
                    <Textarea
                      compact
                      rows={3}
                      value={editDraft}
                      onChange={(event) => setEditDraft(event.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button compact variant="primary" onClick={() => void saveEdit(comment.id)}>
                        Save
                      </Button>
                      <Button compact variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-0.5 whitespace-pre-wrap text-dense text-stone-800">{comment.body}</p>
                )}

                {/* Author-only, matching the server. Everything else about a task
                    is editable by anyone signed in, but these are someone's own
                    words. */}
                {editingId !== comment.id && comment.author?.id === user?.id ? (
                  <div className="mt-0.5 flex gap-3 text-meta">
                    <button
                      type="button"
                      className="text-stone-500 hover:text-stone-700"
                      onClick={() => {
                        setEditingId(comment.id);
                        setEditDraft(comment.body);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="text-stone-500 hover:text-red-700"
                      onClick={() => void remove(comment.id)}
                    >
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 space-y-1.5">
        <Textarea
          compact
          rows={2}
          value={draft}
          placeholder="Leave a comment"
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button compact variant="primary" disabled={saving || !draft.trim()} onClick={() => void post()}>
          Comment
        </Button>
      </div>
    </section>
  );
}
